import { actions, assign, createMachine, InterpreterFrom, StateMachine, StateNodeConfig, StateValueFrom } from "xstate"
import { assertEvent } from "../../xstate-helpers"

import { Ev } from "../event"

export type WorkAction = () => Promise<unknown>

export type WorkItem = {
    key: string
    action: WorkAction
}

type EnqueueEvent = Ev<"ENQUEUE", { key: string; action: WorkAction }>
type SuspendEvent = Ev<"SUSPEND">
type ResumeEvent = Ev<"RESUME">
type Events = EnqueueEvent | SuspendEvent | ResumeEvent

type Context = {
    queue: WorkItem[]
    suspending: boolean
}

export type WorkQueueMachine = StateMachine<
    Context,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    Events,
    WorkQueueTypestates
>
export type WorkQueueService = InterpreterFrom<WorkQueueMachine>
export type WorkQueueState = WorkQueueService["state"]
export type WorkQueueSend = WorkQueueService["send"]
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WorkQueueStateNodeConfig = StateNodeConfig<Context, any, Events>
export type WorkQueueTypestates =
    | { value: "empty"; context: Context }
    | { value: "working"; context: Context }
    | { value: "suspended"; context: Context }
    | { value: "error"; context: Context }
export type WorkQueueStateValue = StateValueFrom<WorkQueueMachine>

// NOTE: some inspiration from this machine https://xstate-catalogue.com/machines/queue
export const workQueueMachine = createMachine<Context, Events, WorkQueueTypestates>(
    {
        id: "work-queue",
        initial: "empty",
        predictableActionArguments: true,
        context: {
            queue: [],
            suspending: false,
        },
        states: {
            empty: {
                on: {
                    ENQUEUE: { target: "working", actions: ["enqueue"] },
                    SUSPEND: { target: "suspended" },
                },
            },
            working: {
                invoke: {
                    src: "asyncWork",
                    onDone: { target: "checkingIfMoreWorkToDo", actions: ["removeOldest"] },
                    onError: { target: "error", actions: ["setError", "logError"] },
                },
                on: {
                    ENQUEUE: { actions: ["enqueue"] },

                    SUSPEND: { actions: ["requestSuspend"] },
                    RESUME: { actions: ["clearSuspendingRequest"] },
                },
            },
            checkingIfMoreWorkToDo: {
                always: [
                    { target: "suspended", cond: "isSuspending" },
                    { target: "working", cond: "hasMoreItemsInQueue" },
                    { target: "empty" },
                ],
            },
            suspended: {
                entry: ["clearSuspendingRequest"],
                on: {
                    ENQUEUE: { actions: ["enqueue"] },
                    RESUME: { target: "checkingIfMoreWorkToDo" },
                },
            },
            error: {},
        },
        /*on: {
            "*": { actions: ["logMe"] },
        },*/
    },
    {
        guards: {
            hasMoreItemsInQueue: (ctx) => ctx.queue.length > 0,
            isSuspending: (ctx) => ctx.suspending,
        },
        actions: {
            enqueue: assign<Context, Events>({
                queue: (ctx, ev) => {
                    assertEvent(ev, "ENQUEUE")
                    return [...ctx.queue, { key: ev.key, action: ev.action }]
                },
            }),
            removeOldest: assign<Context, Events>({
                queue: (ctx) => {
                    const [, ...newQueue] = ctx.queue
                    return newQueue
                },
            }),
            requestSuspend: assign<Context, Events>({
                suspending: true,
            }),
            clearSuspendingRequest: assign<Context, Events>({
                suspending: false,
            }),
            logMe: actions.log<Context, Events>((ctx, ev) => ({ type: ev.type, ctx, ev })),
        },
        services: {
            asyncWork: async (ctx) => {
                const item: WorkItem | undefined = ctx.queue[0]

                if (!item) return

                // console.log("WQ START", item.key)
                const result = await item.action()
                // console.log("WQ END", item.key)
                return result
            },
        },
    },
)
